// Neeko — Blooming Burst VFX: a vibrant orb arcs from unit to unit,
// growing larger and more explosive with each bounce. Color shifts teal → lime → pink.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const TRAVEL_MS  = 370;
const IMPACT_MS  = 210;
const SEGMENT_MS = TRAVEL_MS + IMPACT_MS; // 580ms per bounce

const PALETTE = [
  { orb: "#22ddc8", glow: "#00ffee", impact: "#88ffee" },
  { orb: "#88ee22", glow: "#bbff44", impact: "#ccffaa" },
  { orb: "#ee44cc", glow: "#ff88ee", impact: "#ffbbff" },
] as const;

export class NeekoBounceVFX implements Animation {
  private elapsed = 0;
  private readonly total: number;
  private readonly impactsFired: boolean[];
  onEnd?: () => void;

  constructor(
    private readonly positions: Point[],  // [casterPos, target0, target1, ...]
    private readonly hexSize: number,
    private readonly onHit: (bounceIndex: number) => void,
  ) {
    const bounces = positions.length - 1;
    this.total = bounces * SEGMENT_MS;
    this.impactsFired = new Array(bounces).fill(false);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.total);

    const bounces = this.positions.length - 1;
    for (let i = 0; i < bounces; i++) {
      const impactAt = i * SEGMENT_MS + TRAVEL_MS;
      if (!this.impactsFired[i] && this.elapsed >= impactAt) {
        this.impactsFired[i] = true;
        this.onHit(i);
      }
    }

    if (this.elapsed >= this.total) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const bounces = this.positions.length - 1;
    for (let i = 0; i < bounces; i++) {
      const segElapsed = this.elapsed - i * SEGMENT_MS;
      if (segElapsed <= 0) break;

      const palette = PALETTE[Math.min(i, PALETTE.length - 1)];
      const sizeScale = 1 + i * 0.28;
      const from = this.positions[i];
      const to   = this.positions[i + 1];

      if (segElapsed < TRAVEL_MS) {
        const t = Easing.easeOutQuad(segElapsed / TRAVEL_MS);
        const x = from.x + (to.x - from.x) * t;
        const arc = Math.sin(t * Math.PI) * this.hexSize * 0.6;
        const y = from.y + (to.y - from.y) * t - arc;

        this.drawTrail(ctx, from, { x, y }, palette, t);
        this.drawOrb(ctx, x, y, palette, sizeScale);
      } else if (segElapsed < SEGMENT_MS) {
        const t = (segElapsed - TRAVEL_MS) / IMPACT_MS;
        this.drawImpact(ctx, to.x, to.y, t, palette, sizeScale);
      }
    }
  }

  private drawOrb(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    p: typeof PALETTE[number],
    scale: number,
  ): void {
    const r = this.hexSize * 0.28 * scale;
    ctx.save();

    ctx.globalAlpha = 0.42;
    ctx.fillStyle = p.glow;
    ctx.shadowColor = p.glow;
    ctx.shadowBlur = 36;
    ctx.beginPath();
    ctx.arc(x, y, r * 2.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.fillStyle = p.orb;
    ctx.shadowColor = p.glow;
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 0.85;
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.32, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawTrail(
    ctx: CanvasRenderingContext2D,
    from: Point,
    to: { x: number; y: number },
    p: typeof PALETTE[number],
    t: number,
  ): void {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.hypot(dx, dy) < 6) return;

    ctx.save();
    ctx.globalAlpha = (1 - t) * 0.3;
    ctx.strokeStyle = p.glow;
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.shadowColor = p.glow;
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
  }

  private drawImpact(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    t: number,
    p: typeof PALETTE[number],
    scale: number,
  ): void {
    const fade = 1 - Easing.easeInQuad(t);
    ctx.save();

    // Initial white flash
    if (t < 0.25) {
      const flash = 1 - t / 0.25;
      ctx.globalAlpha = flash * 0.65;
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = p.glow;
      ctx.shadowBlur = 44;
      ctx.beginPath();
      ctx.arc(x, y, this.hexSize * 0.75 * flash * scale, 0, Math.PI * 2);
      ctx.fill();
    }

    // Expanding bloom ring
    const ringR = this.hexSize * (0.25 + t * 2.0) * scale;
    ctx.globalAlpha = fade * 0.92;
    ctx.strokeStyle = p.orb;
    ctx.lineWidth = 3.2 * (1 - t * 0.55);
    ctx.shadowColor = p.glow;
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(x, y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // Second softer ring
    const ring2R = this.hexSize * (0.1 + t * 1.2) * scale;
    ctx.globalAlpha = fade * 0.5;
    ctx.strokeStyle = p.impact;
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(x, y, ring2R, 0, Math.PI * 2);
    ctx.stroke();

    // Petal burst — 6 radiating lines
    const petalLen = this.hexSize * Easing.easeOutCubic(t) * 1.6 * scale;
    ctx.globalAlpha = fade * 0.78;
    ctx.strokeStyle = p.impact;
    ctx.lineWidth = 2;
    ctx.shadowColor = p.glow;
    ctx.shadowBlur = 16;
    ctx.lineCap = "round";
    for (let k = 0; k < 6; k++) {
      const angle = (k / 6) * Math.PI * 2 + t * 0.4;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(angle) * petalLen, y + Math.sin(angle) * petalLen);
      ctx.stroke();
    }

    // Filled center blossom (shrinks out)
    if (t < 0.45) {
      const inner = (0.45 - t) / 0.45;
      ctx.globalAlpha = inner * 0.55;
      ctx.fillStyle = p.orb;
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.arc(x, y, this.hexSize * 0.5 * inner * scale, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.total;
  }
}
